import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import type { loader } from '~/routes/_app.product.$urlSegment';
import { type NumberedAccordionRow } from '~/ui-rocco/pages/detail-shared';
import { AwardsRowContent } from './AwardsRowContent';

/**
 * CMS tabs that duplicate built-in accordion rows: "Specifications" is the
 * Product Details table's Specs value, and "Product Range" duplicates the
 * built-in "Other Products in this range" carousel. Case-insensitive.
 */
export interface ProductAccordionRows {
    rows: NumberedAccordionRow[];
    defaultOpenTitles: string[] | undefined;
}

/**
 * Builds the numbered spec-accordion rows from the product (Figma 1846:9004 /
 * 9046 / 9068 / 9088). Shared so the mobile standalone Similar Products section
 * can read `rows.length` to continue the "(NN)" numbering — the numbered
 * sequence is exactly these accordion rows.
 *
 * Product Details is followed by the three action cards — website, enquiry
 * and contact — then the numbered content rows.
 *
 * About the Supplier lives inside the accordion on both breakpoints; Similar
 * Products joins it on desktop (default open) but stays a standalone mobile
 * section, so on mobile it is absent here and continues the numbering itself.
 * Installers is its own section — see ProductInstallersSection.
 */
export const useProductAccordionRows = (): ProductAccordionRows => {
    const { product } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();

    const rows: NumberedAccordionRow[] = [];

    if (product.Awards.length > 0) {
        rows.push({
            title: 'Awards and Recognition',
            content: <AwardsRowContent awards={product.Awards} />,
        });
    }

    // Specified in — where this product has been used. Reads after the
    // supplier: the rows above answer what the product IS and who makes it,
    // and this is the evidence on top of that. Desktop only, because mobile
    // keeps the standalone statement treatment (see SpecifiedInSection) —
    // the click-to-expand reads as plain text on touch.
    //
    // Gated on the SSR'd first page so the row only appears (and is numbered)
    // when there is a project to show, the same way Similar Products is.
    // Desktop opens every row that reads as page content on load — only the
    // action rows and the conditional evidence rows stay closed. Mobile
    // (single mode) opens Product Details only.
    const defaultOpenTitles = mobile
        ? []
        : [
              'Other Products in this range',
              `More from ${product.Professional?.Title ?? ''}`,
          ];

    return { rows, defaultOpenTitles };
};
